import { Fragment } from "react";
import { useParams } from "react-router";
import { styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { Card, KpiHeadline, MetaLabel } from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { PageLayout } from "@acto/taskpane/field-sales-2/components/patterns/PageLayout";
import { useDetailTopBar } from "@acto/taskpane/field-sales-2/components/patterns/DetailTopBar/DetailTopBar";
import {
  LineItemsCard,
  OrderHeading,
  OrderStatusFigures,
  useLineItemGroups,
} from "@acto/taskpane/field-sales-2/components/patterns/order-details";
import { formatNumericDate } from "@acto/taskpane/field-sales-2/utils/format";
import {
  type OrderDetails as OrderDetailsData,
  useOrderDetails,
} from "@acto/taskpane/field-sales-2/data-access/useOrderDetails";

const SectionTitle = styled("div")(({ theme }) => ({
  padding: `${theme.fieldSalesTwo.spacing[20]} 0 ${theme.fieldSalesTwo.spacing[4]} ${theme.fieldSalesTwo.spacing[20]}`,
}));

function LineItemSections({ order }: { order: OrderDetailsData }) {
  const groups = useLineItemGroups(order);

  return groups.map((group) => (
    <Fragment key={group.key}>
      <SectionTitle>
        <OrderHeading>{group.title}</OrderHeading>
      </SectionTitle>
      <LineItemsCard items={group.items} />
    </Fragment>
  ));
}

export function OrderDetails() {
  const t = useTranslations("fieldSalesTwo");
  const { locale } = useLocale();
  const { orderId } = useParams<{ orderId: string }>();

  const { isPending, error, order } = useOrderDetails(orderId);
  const { sentinelRef, topBar } = useDetailTopBar({
    title: order?.externalReference,
    subtitle: order?.formattedIncomingOrder,
    backFallbackUrl: "/home",
  });

  return (
    <>
      {topBar}
      <PageLayout hPadding="card" isLoading={isPending} isError={!!error} isEmpty={!order}>
        {order && (
          <>
            <Card>
              <OrderHeading ref={sentinelRef}>{order.externalReference}</OrderHeading>
              <Divider />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <MetaLabel>
                  {t("orderDetails.valueLabel")} · {formatNumericDate(order.date, locale)}
                </MetaLabel>
                <KpiHeadline>{order.formattedIncomingOrder}</KpiHeadline>
              </div>
              <OrderStatusFigures order={order} />
            </Card>
            <LineItemSections order={order} />
          </>
        )}
      </PageLayout>
    </>
  );
}
